import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { joinUrls } from '@utils/urls';
import { catchError, EMPTY, map, Observable, of, shareReplay, switchMap } from 'rxjs';

export function setCacheToMap(map: Map<string, Observable<any>>, key: string) {
  return function <T extends Observable<any>>(source: T): T {
    return new Observable((subscriber) => {
      return source.subscribe({
        next(value) {
          map.set(key, of(value).pipe(shareReplay(1)));
          subscriber.next(value);
        },
        error(err) {
          subscriber.error(err);
        },
        complete() {
          subscriber.complete();
        },
      });
    }) as T;
  };
}

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private http = inject(HttpClient);

  private settingsCache = new Map<string, Observable<any>>();

  get<T>(name: string, fallback: T | Array<never> | null = []): Observable<T> {
    if (!this.hasCache(name)) {
      this.settingsCache.set(
        name,
        this.http
          .get<any>(joinUrls(environment.urls.user, environment.urls.settings), {
            params: { key: name },
          })
          .pipe(
            map((res) => res[name] ?? fallback),
            shareReplay(1),
          ),
      );
    }
    return this.settingsCache.get(name) as Observable<T>;
  }

  save<T>(name: string, settings: T): Observable<T> {
    if (this.settingsCache.has(name)) {
      return this.settingsCache.get(name)!.pipe(switchMap((cache) => this.sendUpdateRequest(settings, name)));
    }

    return this.sendUpdateRequest(settings, name);
  }

  protected sendUpdateRequest<T>(settings: T, name: string): Observable<T> {
    return this.http
      .put<any>(joinUrls(environment.urls.user, environment.urls.settings), {
        user_settings: { [name]: settings },
      })
      .pipe(
        map((res) => res[name]),
        setCacheToMap(this.settingsCache, name),
        catchError(() => EMPTY),
      );
  }

  protected hasCache(name: string): boolean {
    return this.settingsCache.has(name);
  }
}
